import type { PaginatedResponse } from '@kizen/api';
import type { Form, FormListItem, GetFormsParams } from '@kizen/api/form';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import { getForm, getForms } from '@kizen/api/form';
import { getResource } from './resource';
import { curry1 } from './utils';

type FormQuery<TData = Form> = WithRequired<
  QueryObserverOptions<
    Form,
    DefaultError,
    TData,
    Form,
    [Resource<Readonly<['forms', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type SurveyQuery<TData = Form> = WithRequired<
  QueryObserverOptions<
    Form,
    DefaultError,
    TData,
    Form,
    [Resource<Readonly<['surveys', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type FormsQuery<TData = PaginatedResponse<FormListItem>> = WithRequired<
  QueryObserverOptions<
    PaginatedResponse<FormListItem>,
    DefaultError,
    TData,
    PaginatedResponse<FormListItem>,
    [Resource<Readonly<['forms']>, GetFormsParams>]
  >,
  'queryFn' | 'queryKey'
>;

type SurveysQuery<TData = PaginatedResponse<FormListItem>> = WithRequired<
  QueryObserverOptions<
    PaginatedResponse<FormListItem>,
    DefaultError,
    TData,
    PaginatedResponse<FormListItem>,
    [Resource<Readonly<['surveys']>, GetFormsParams>]
  >,
  'queryFn' | 'queryKey'
>;

export const formQuery = <TData = Form>(
  axios: AxiosInstance,
  formId: string,
  options: Omit<FormQuery<TData>, 'queryFn' | 'queryKey'> = {}
): FormQuery<TData> => {
  const path = ['forms', formId] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getForm(axios, 'forms', qk.path[1], { signal });
    },
    ...options,
  };
};

export const formsQuery = <TData = PaginatedResponse<FormListItem>>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<FormsQuery<TData>, 'queryFn' | 'queryKey'>,
    GetFormsParams
  > = {}
): FormsQuery<TData> => {
  const path = ['forms'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getForms(axios, 'forms', { params: qk.params, signal });
    },
    ...queryOpts,
  };
};

export const surveyQuery = <TData = Form>(
  axios: AxiosInstance,
  surveyId: string,
  options: Omit<SurveyQuery<TData>, 'queryFn' | 'queryKey'> = {}
): SurveyQuery<TData> => {
  const path = ['surveys', surveyId] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getForm(axios, 'surveys', qk.path[1], { signal });
    },
    ...options,
  };
};

export const surveysQuery = <TData = PaginatedResponse<FormListItem>>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<SurveysQuery<TData>, 'queryFn' | 'queryKey'>,
    GetFormsParams
  > = {}
): SurveysQuery<TData> => {
  const path = ['surveys'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getForms(axios, 'surveys', { params: qk.params, signal });
    },
    ...queryOpts,
  };
};

class Queries {
  public formQuery: <TData = Form>(
    ...args: Drop<Parameters<typeof formQuery<TData>>>
  ) => FormQuery<TData>;
  public formsQuery: <TData = PaginatedResponse<FormListItem>>(
    ...args: Drop<Parameters<typeof formsQuery<TData>>>
  ) => FormsQuery<TData>;
  public surveyQuery: <TData = Form>(
    ...args: Drop<Parameters<typeof surveyQuery<TData>>>
  ) => SurveyQuery<TData>;
  public surveysQuery: <TData = PaginatedResponse<FormListItem>>(
    ...args: Drop<Parameters<typeof surveysQuery<TData>>>
  ) => SurveysQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.formQuery = curry1(formQuery)(axios);
    this.formsQuery = curry1(formsQuery)(axios);
    this.surveyQuery = curry1(surveyQuery)(axios);
    this.surveysQuery = curry1(surveysQuery)(axios);
  }
}

export type FormQueries = Queries;

export const defFormQueries = (axios: AxiosInstance): FormQueries => {
  return new Queries(axios);
};
